/**
 * URL → `chatSource` when Message Center is open on a deal route.
 * Pages usually pass `chatSource` from props; see `getRouteBySource` for source → URL.
 *
 * Resolvers run **in order** — `reduce` + `??` yields the first non-null (later resolvers are not called).
 * When adding a route, append a small function and register it in `DEAL_MESSENGER_ROUTE_RESOLVERS`
 * (do not reorder blindly: more specific paths should stay before generic ones).
 */
import { EMessengerChatSourceType, type IMessengerChatSource } from '@xometry/ui';
import { getDocumentTitle } from 'components/shared/MessengerMessages/utils';
import { decoratedSourceType } from 'components/utils/formatters';
import { ObjectType } from 'interfaces';
import { matchPath } from 'react-router-dom';
import { routes } from 'routes';
import type { RootStore } from 'stores/RootStore';

/**
 * Pipedrive deal id used to build object labels (`QC-{pd}-{id}`, `PI-{pd}-{id}`, …) that match the deal left
 * sidebar (`DealSidebarStore`). Falls back to the numeric deal id, then the route id. Used for the title
 * fallbacks of objects without a dedicated store number (QC, Claim, Payout, Payment, OI, II), so the messenger
 * header shows the full ID including the deal number rather than just `QC 1432` (EUO-231 Bug 2, 28.05 retest).
 */
const pdId = (store: RootStore, dealId: string): string =>
  String(store.dealStore.data?.pipedriveId ?? store.dealStore.data?.id ?? dealId);

const dealTitle = (store: RootStore, dealId: string): string => `D-${pdId(store, dealId)}`;

interface TitleFromStoreArgs {
  store: RootStore;
  sourceType: EMessengerChatSourceType;
  /**
   * Route-derived fallback passed to `getDocumentTitle` as `objectTitle`. Source types that use
   * `objectTitle || 'Generic'` (e.g., RFQ → `'RFQ'`) receive the ID-qualified fallback (`'RFQ 1594'`) instead.
   */
  fallback: string;
  /**
   * Forwarded so `getDocumentTitle` can confirm the per-deal MobX store actually holds the requested object
   * before trusting its label. Without it, the store lags one navigation behind, and the chat header showed
   * the previous object's number (or `J--` before a load) — EUO-231 Bug 2 retest.
   */
  sourceId: string | number;
}

const titleFromStore = ({ store, sourceType, fallback, sourceId }: TitleFromStoreArgs): string =>
  getDocumentTitle({ store, sourceType, objectTitle: fallback, sourceId }) || fallback;

interface BuildDealMessengerChatArgs {
  store: RootStore;
  dealId: string;
  partial: Omit<IMessengerChatSource, 'dealId' | 'dealTitle'>;
}

const buildChat = ({ store, dealId, partial }: BuildDealMessengerChatArgs): IMessengerChatSource => ({
  dealId: String(dealId),
  dealTitle: dealTitle(store, dealId),
  ...partial,
});

export interface BuildDealMessengerChatSourceArgs {
  pathname: string;
  search: string;
  dealId: string;
  store: RootStore;
}

type MatchFn = <T extends Record<string, string>>(
  pathPattern: string,
  exact?: boolean,
) => ReturnType<typeof matchPath<T>>;

export interface DealMessengerRouteResolverCtx extends BuildDealMessengerChatSourceArgs {
  sp: URLSearchParams;
  match: MatchFn;
}

type DealMessengerRouteResolver = (ctx: DealMessengerRouteResolverCtx) => IMessengerChatSource | null;

const resolveDealMain: DealMessengerRouteResolver = ({ store, dealId, match }) => {
  if (!match(routes.dealPath(':dealId'), true)) return null;

  return buildChat({
    store,
    dealId,
    partial: {
      id: String(dealId),
      sourceId: String(dealId),
      sourceType: EMessengerChatSourceType.DEAL,
      title: titleFromStore({
        store,
        sourceType: EMessengerChatSourceType.DEAL,
        fallback: dealTitle(store, dealId),
        sourceId: String(dealId),
      }),
    },
  });
};

const resolveOrderConfirmation: DealMessengerRouteResolver = ({ store, dealId, match }) => {
  if (!match(routes.orderConfirmationPath(':dealId'), true)) return null;

  const ocId = store.orderConfirmationStore.orderConfirmation?.id;

  if (!ocId) return null;

  return buildChat({
    store,
    dealId,
    partial: {
      id: String(ocId),
      sourceId: String(ocId),
      sourceType: EMessengerChatSourceType.ORDER_CONFIRMATION,
      title: titleFromStore({
        store,
        sourceType: EMessengerChatSourceType.ORDER_CONFIRMATION,
        fallback: `OC #${ocId}`,
        sourceId: String(ocId),
      }),
    },
  });
};

const resolvePayment: DealMessengerRouteResolver = ({ store, dealId, match }) => {
  const pay = match<{ dealId: string; paymentId: string }>(routes.editPaymentPath(':dealId', ':paymentId'), true);

  if (!pay?.params?.paymentId || pay.params.paymentId === 'new') return null;

  const pid = pay.params.paymentId;

  return buildChat({
    store,
    dealId,
    partial: {
      id: pid,
      sourceId: pid,
      sourceType: EMessengerChatSourceType.PAYMENT,
      title: titleFromStore({
        store,
        sourceType: EMessengerChatSourceType.PAYMENT,
        fallback: `PI-${pdId(store, dealId)}-${pid}`,
        sourceId: pid,
      }),
    },
  });
};

const resolveDealPart: DealMessengerRouteResolver = ({ store, dealId, match, sp }) => {
  if (!match(routes.dealPartsPath(':dealId'), false)) return null;

  const partId = sp.get('part_id');

  if (!partId) return null;

  return buildChat({
    store,
    dealId,
    partial: {
      id: partId,
      sourceId: partId,
      sourceType: EMessengerChatSourceType.PART,
      title: titleFromStore({
        store,
        sourceType: EMessengerChatSourceType.PART,
        fallback: `Part ${partId}`,
        sourceId: partId,
      }),
    },
  });
};

const resolveEnquiry: DealMessengerRouteResolver = ({ store, dealId, match }) => {
  const enq = match<{ dealId: string; enquiryId: string }>(routes.enquiryEditPath(':dealId', ':enquiryId'), true);

  if (!enq?.params?.enquiryId) return null;

  const id = enq.params.enquiryId;

  return buildChat({
    store,
    dealId,
    partial: {
      id,
      sourceId: id,
      sourceType: EMessengerChatSourceType.ENQUIRY,
      title: titleFromStore({ store, sourceType: EMessengerChatSourceType.ENQUIRY, fallback: 'Enquiry', sourceId: id }),
    },
  });
};

const resolveProviderOrder: DealMessengerRouteResolver = ({ store, dealId, match }) => {
  const po = match<{ dealId: string; providerOrderId: string }>(
    routes.editProviderOrderPath(':dealId', ':providerOrderId'),
    true,
  );

  if (!po?.params?.providerOrderId) return null;

  const id = po.params.providerOrderId;

  return buildChat({
    store,
    dealId,
    partial: {
      id,
      sourceId: id,
      sourceType: EMessengerChatSourceType.PROVIDER_ORDER,
      title: titleFromStore({
        store,
        sourceType: EMessengerChatSourceType.PROVIDER_ORDER,
        fallback: `PO-${pdId(store, dealId)}-${id}`,
        sourceId: id,
      }),
    },
  });
};

const resolveOutcomingInvoice: DealMessengerRouteResolver = ({ store, dealId, match }) => {
  const inv = match<{ dealId: string; invoiceId: string }>(
    routes.editOutcomingInvoicePath(':dealId', ':invoiceId'),
    true,
  );

  if (!inv?.params?.invoiceId) return null;

  const id = inv.params.invoiceId;

  return buildChat({
    store,
    dealId,
    partial: {
      id,
      sourceId: id,
      sourceType: EMessengerChatSourceType.OUTCOMING_INVOICE,
      title: titleFromStore({
        store,
        sourceType: EMessengerChatSourceType.OUTCOMING_INVOICE,
        fallback: `OI-${pdId(store, dealId)}-${id}`,
        sourceId: id,
      }),
    },
  });
};

const resolveIncomingInvoice: DealMessengerRouteResolver = ({ store, dealId, match }) => {
  const inc = match<{ dealId: string; invoiceId: string }>(
    routes.editDealIncomingInvoicePath(':dealId', ':invoiceId'),
    true,
  );

  if (!inc?.params?.invoiceId) return null;

  const id = inc.params.invoiceId;

  return buildChat({
    store,
    dealId,
    partial: {
      id,
      sourceId: id,
      sourceType: EMessengerChatSourceType.INCOMING_INVOICE,
      title: titleFromStore({
        store,
        sourceType: EMessengerChatSourceType.INCOMING_INVOICE,
        fallback: `II-${pdId(store, dealId)}-${id}`,
        sourceId: id,
      }),
    },
  });
};

const resolveQualityControl: DealMessengerRouteResolver = ({ store, dealId, match }) => {
  const qc = match<{ dealId: string; qcId: string }>(routes.editDealQualityControlPath(':dealId', ':qcId'), true);

  if (!qc?.params?.qcId) return null;

  const id = qc.params.qcId;

  return buildChat({
    store,
    dealId,
    partial: {
      id,
      sourceId: id,
      sourceType: EMessengerChatSourceType.QUALITY_CONTROL,
      title: titleFromStore({
        store,
        sourceType: EMessengerChatSourceType.QUALITY_CONTROL,
        fallback: `QC-${pdId(store, dealId)}-${id}`,
        sourceId: id,
      }),
    },
  });
};

const resolveShipping: DealMessengerRouteResolver = ({ store, dealId, match }) => {
  const sh = match<{ dealId: string; shippingId: string }>(routes.editShippingPath(':dealId', ':shippingId'), true);

  if (!sh?.params?.shippingId) return null;

  const id = sh.params.shippingId;
  const loaded = store.dealShippingStore.shipping;
  const pd = store.dealStore.data?.pipedriveId ?? store.dealStore.data?.id ?? dealId;
  /**
   * `getDocumentTitle` reads `dealShippingStore.shipping.number` without checking that the loaded row matches
   * the shipping id in the URL — after a sidebar switch the store can still hold the previous shipping, so the
   * messenger title lagged one navigation. Prefer the store label only when ids match; otherwise use the same
   * `SH-{pd}-{id}` fallback as the deal sidebar (`DealSidebarStore`).
   */
  const title =
    loaded?.id != null && String(loaded.id) === String(id) && loaded.number
      ? loaded.number
      : decoratedSourceType(ObjectType.SH, id, pd);

  return buildChat({
    store,
    dealId,
    partial: {
      id,
      sourceId: id,
      sourceType: EMessengerChatSourceType.SHIPPING,
      title,
    },
  });
};

const resolveStorage: DealMessengerRouteResolver = ({ store, dealId, match }) => {
  const st = match<{ dealId: string; storageId: string }>(routes.editStoragePath(':dealId', ':storageId'), true);

  if (!st?.params?.storageId) return null;

  const id = st.params.storageId;

  return buildChat({
    store,
    dealId,
    partial: {
      id,
      sourceId: id,
      sourceType: EMessengerChatSourceType.STORAGE,
      title: titleFromStore({
        store,
        sourceType: EMessengerChatSourceType.STORAGE,
        fallback: `SU-${pdId(store, dealId)}-${id}`,
        sourceId: id,
      }),
    },
  });
};

const resolveClaim: DealMessengerRouteResolver = ({ store, dealId, match }) => {
  const cl = match<{ dealId: string; claimId: string }>(routes.editDealClaimPath(':dealId', ':claimId'), true);

  if (!cl?.params?.claimId) return null;

  const id = cl.params.claimId;

  return buildChat({
    store,
    dealId,
    partial: {
      id,
      sourceId: id,
      sourceType: EMessengerChatSourceType.CLAIM,
      title: titleFromStore({
        store,
        sourceType: EMessengerChatSourceType.CLAIM,
        fallback: `CL-${pdId(store, dealId)}-${id}`,
        sourceId: id,
      }),
    },
  });
};

const resolveJob: DealMessengerRouteResolver = ({ store, dealId, match }) => {
  const job = match<{ dealId: string; jobId: string }>(routes.jobPath(':dealId', ':jobId'), true);

  if (!job?.params?.jobId) return null;

  const id = job.params.jobId;

  return buildChat({
    store,
    dealId,
    partial: {
      id,
      sourceId: id,
      sourceType: EMessengerChatSourceType.JOB,
      title: titleFromStore({
        store,
        sourceType: EMessengerChatSourceType.JOB,
        fallback: `J-${pdId(store, dealId)}-${id}`,
        sourceId: id,
      }),
    },
  });
};

const resolveRfqOffer: DealMessengerRouteResolver = ({ store, dealId, match }) => {
  const offer = match<{ dealId: string; rfqId: string; rfqOfferId: string }>(
    routes.rfqOfferPath(':dealId', ':rfqId', ':rfqOfferId'),
    true,
  );

  if (!offer?.params?.rfqOfferId || !offer?.params?.rfqId) return null;

  const rfqId = offer.params.rfqId;
  const id = offer.params.rfqOfferId;

  return buildChat({
    store,
    dealId,
    partial: {
      id,
      sourceId: id,
      sourceType: EMessengerChatSourceType.RFQ_OFFER,
      title: titleFromStore({
        store,
        sourceType: EMessengerChatSourceType.RFQ_OFFER,
        fallback: 'RFQ Offer',
        sourceId: id,
      }),
      parent: {
        dealId: String(dealId),
        sourceType: EMessengerChatSourceType.RFQ,
        sourceId: rfqId,
      },
    },
  });
};

const resolveRfq: DealMessengerRouteResolver = ({ store, dealId, match }) => {
  const rfq = match<{ dealId: string; rfqId: string }>(routes.rfqEditPath(':dealId', ':rfqId'), true);

  if (!rfq?.params?.rfqId) return null;

  const id = rfq.params.rfqId;

  return buildChat({
    store,
    dealId,
    partial: {
      id,
      sourceId: id,
      sourceType: EMessengerChatSourceType.RFQ,
      title: titleFromStore({ store, sourceType: EMessengerChatSourceType.RFQ, fallback: `RFQ ${id}`, sourceId: id }),
    },
  });
};

const resolvePayout: DealMessengerRouteResolver = ({ store, dealId, match }) => {
  const payout = match<{ dealId: string; payoutId: string }>(routes.editPayoutPath(':dealId', ':payoutId'), true);

  if (!payout?.params?.payoutId) return null;

  const id = payout.params.payoutId;

  return buildChat({
    store,
    dealId,
    partial: {
      id,
      sourceId: id,
      sourceType: EMessengerChatSourceType.PAYOUT,
      title: titleFromStore({
        store,
        sourceType: EMessengerChatSourceType.PAYOUT,
        fallback: `PZ-${pdId(store, dealId)}-${id}`,
        sourceId: id,
      }),
    },
  });
};

const DEAL_MESSENGER_ROUTE_RESOLVERS: ReadonlyArray<DealMessengerRouteResolver> = [
  resolveDealMain,
  resolveOrderConfirmation,
  resolvePayment,
  resolveDealPart,
  resolveEnquiry,
  resolveProviderOrder,
  resolveOutcomingInvoice,
  resolveIncomingInvoice,
  resolveQualityControl,
  resolveShipping,
  resolveStorage,
  resolveClaim,
  resolveJob,
  resolveRfqOffer,
  resolveRfq,
  resolvePayout,
];

export const buildDealMessengerChatSource = ({
  pathname,
  search,
  dealId,
  store,
}: BuildDealMessengerChatSourceArgs): IMessengerChatSource | null => {
  const sp = new URLSearchParams(search);

  const match: MatchFn = <T extends Record<string, string>>(pathPattern: string, exact?: boolean) =>
    matchPath<T>(pathname, { path: pathPattern, exact });

  const ctx: DealMessengerRouteResolverCtx = { pathname, search, dealId, store, sp, match };

  return DEAL_MESSENGER_ROUTE_RESOLVERS.reduce<IMessengerChatSource | null>(
    (found, resolve) => found ?? resolve(ctx),
    null,
  );
};
